const STORAGE_KEY = 'registrationFormDraft';

/**
 * @param {string} key
 * @returns {string | null}
 */
function safeSessionStorageGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string} value
 * @returns {boolean}
 */
function safeSessionStorageSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {string} key
 * @returns {boolean}
 */
function safeSessionStorageRemove(key) {
  try {
    sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/** @typedef {{
 *   dni: string;
 *   nombre: string;
 *   primerApellido: string;
 *   segundoApellido: string;
 *   fechaNacimiento: string;
 *   direccion: string;
 *   ciudad: string;
 *   codigoPostal: string;
 *   telefono: string;
 *   email: string;
 *   cursos: string[];
 *   cuponCodigo: string;
 *   consentAccepted: boolean;
 * }} RegistrationFormDraft
 */

/** @type {RegistrationFormDraft} */
export const EMPTY_REGISTRATION_FORM = {
  dni: '',
  nombre: '',
  primerApellido: '',
  segundoApellido: '',
  fechaNacimiento: '',
  direccion: '',
  ciudad: '',
  codigoPostal: '',
  telefono: '',
  email: '',
  cursos: [],
  cuponCodigo: '',
  consentAccepted: false,
};

/**
 * @returns {RegistrationFormDraft}
 */
export function loadRegistrationFormDraft() {
  const raw = safeSessionStorageGet(STORAGE_KEY);
  if (!raw) {
    return { ...EMPTY_REGISTRATION_FORM, cursos: [] };
  }

  try {
    const parsed = JSON.parse(raw);
    return normalizeRegistrationFormDraft(parsed) ?? { ...EMPTY_REGISTRATION_FORM, cursos: [] };
  } catch {
    return { ...EMPTY_REGISTRATION_FORM, cursos: [] };
  }
}

/**
 * @param {RegistrationFormDraft} form
 */
export function saveRegistrationFormDraft(form) {
  if (!form) {
    return;
  }

  const persisted = extractPersistedRegistrationDraft(form);
  if (!persisted) {
    return;
  }

  safeSessionStorageSet(STORAGE_KEY, JSON.stringify(persisted));
}

/**
 * Keeps only course IDs that still exist in the current public catalog.
 * @param {string[]} selectedIds
 * @param {string[]} availableCourseIds
 * @returns {string[]}
 */
export function filterRegistrationFormCourseIds(selectedIds, availableCourseIds) {
  const available = new Set(
    Array.isArray(availableCourseIds)
      ? availableCourseIds.filter((id) => typeof id === 'string' && id.length > 0)
      : [],
  );

  if (!Array.isArray(selectedIds)) {
    return [];
  }

  return selectedIds.filter((id) => typeof id === 'string' && id.length > 0 && available.has(id));
}

export function clearRegistrationFormDraft() {
  safeSessionStorageRemove(STORAGE_KEY);
}

/**
 * @param {RegistrationFormDraft | Record<string, string | boolean | string[] | undefined> | null | undefined} source
 * @returns {RegistrationFormDraft | null}
 */
function extractPersistedRegistrationDraft(source) {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const cursos = Array.isArray(source.cursos)
    ? source.cursos
      .filter((id) => typeof id === 'string' && id.trim().length > 0)
      .map((id) => id.trim())
    : [];

  return {
    dni: String(source.dni || ''),
    nombre: String(source.nombre || ''),
    primerApellido: String(source.primerApellido || ''),
    segundoApellido: String(source.segundoApellido || ''),
    fechaNacimiento: String(source.fechaNacimiento || ''),
    direccion: String(source.direccion || ''),
    ciudad: String(source.ciudad || '').trim(),
    codigoPostal: String(source.codigoPostal || '').trim(),
    telefono: String(source.telefono || ''),
    email: String(source.email || '').trim().toLowerCase(),
    cursos,
    cuponCodigo: String(source.cuponCodigo || '').trim(),
    consentAccepted: source.consentAccepted === true,
  };
}

/**
 * @param {RegistrationFormDraft | Record<string, string | boolean | string[] | undefined> | null | undefined} source
 * @returns {RegistrationFormDraft | null}
 */
function normalizeRegistrationFormDraft(source) {
  return extractPersistedRegistrationDraft(source);
}
